import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Input } from './ui/input.jsx';
import { Plus, Search, BookOpen, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

/**
 * Autocomplete de cargos a partir do catálogo compartilhado.
 *
 * Props:
 *  - value: string (nome atual digitado)
 *  - onSelect(cargo): chamado quando usuário escolhe um cargo do catálogo (recebe doc completo)
 *  - onChange(text): chamado a cada digitação livre
 *  - testId: prefixo de data-testid
 */
const buildSubtitle = (c) => {
  const parts = [];
  if (c.setor) parts.push(c.setor);
  if (c.cbo) parts.push(`CBO ${c.cbo}`);
  if (c.descricao_sumaria) parts.push(c.descricao_sumaria);
  return parts.join(' · ');
};

const CargoAutocomplete = ({ value, onChange, onSelect, testId = 'cargo-input', placeholder = 'Ex: Analista de RH Pleno', className = '', excludedNames = [], onCatalogReady = null }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // Carrega catálogo inicial uma vez
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/cargos-catalogo?limit=500`, auth());
        if (mounted) {
          const itemsData = data.items || [];
          setAllItems(itemsData);
          setItems(itemsData);
          if (onCatalogReady) onCatalogReady(itemsData);
        }
      } catch (e) { /* silent */ }
    })();
    return () => { mounted = false; };
  }, []);

  // Filtra localmente (rápido) — não precisa hit no servidor
  useEffect(() => {
    const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    const excludedSet = new Set((excludedNames || []).map(n => norm(n)).filter(Boolean));
    const currentNorm = norm(value);
    const filterExcluded = (c) => {
      const cn = norm(c.nome);
      // não esconde o próprio item se for igual ao valor já digitado (permite re-seleção)
      if (cn === currentNorm) return true;
      return !excludedSet.has(cn);
    };
    if (!value || !value.trim()) {
      setItems(allItems.filter(filterExcluded));
      return;
    }
    const qn = norm(value);
    setItems(allItems.filter(c => norm(c.nome).includes(qn)).filter(filterExcluded).slice(0, 50));
    setHighlight(0);
  }, [value, allItems, excludedNames]);

  // Click fora fecha
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleSelect = (cargo) => {
    onChange(cargo.nome);
    if (onSelect) onSelect(cargo);
    setOpen(false);
  };

  const handleKey = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && items[highlight]) { e.preventDefault(); handleSelect(items[highlight]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  const exactMatch = allItems.some(c => c.nome.toLowerCase() === (value || '').trim().toLowerCase());
  const showCreateHint = value && value.trim() && !exactMatch;

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
        <Input
          value={value || ''}
          onChange={e => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKey}
          placeholder={placeholder}
          className={`h-8 text-sm pl-7 ${className}`}
          data-testid={testId}
          autoComplete="off"
        />
      </div>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid={`${testId}-dropdown`}>
          <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-100 flex items-center gap-1.5 text-[10px] text-slate-500 font-medium">
            <BookOpen className="w-3 h-3" />
            Catálogo de Cargos {value ? `· ${items.length} resultado(s)` : `· ${allItems.length} cargos`}
          </div>
          {items.length === 0 && !showCreateHint && (
            <div className="px-3 py-3 text-xs text-slate-400">Nenhum cargo encontrado</div>
          )}
          {items.map((c, i) => (
            <button
              key={c.id}
              type="button"
              onMouseEnter={() => setHighlight(i)}
              onClick={() => handleSelect(c)}
              data-testid={`${testId}-option-${i}`}
              className={`w-full text-left px-3 py-2 transition-colors flex items-center justify-between gap-2 border-b border-slate-50 last:border-0 ${i === highlight ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900 truncate">{c.nome}</p>
                {buildSubtitle(c) && <p className="text-[10px] text-slate-500 truncate">{buildSubtitle(c)}</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {c.usage_count > 0 && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">{c.usage_count}x</span>
                )}
                {value && c.nome.toLowerCase() === value.trim().toLowerCase() && (
                  <Check className="w-3 h-3 text-emerald-500" />
                )}
              </div>
            </button>
          ))}
          {showCreateHint && (
            <div className="px-3 py-2 bg-amber-50 border-t border-amber-200 flex items-center gap-1.5 text-[11px] text-amber-700" data-testid={`${testId}-create-hint`}>
              <Plus className="w-3 h-3" />
              <span>Novo cargo: <strong>"{value}"</strong> será adicionado ao catálogo ao salvar</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CargoAutocomplete;
